
(() => {
  const ROOT_SELECTOR =
    '[data-layered-slider-init]';

  const TITLE_SELECTOR =
    '[data-layered-slider-title]';

  const BACKGROUND_SELECTOR =
    '[data-layered-slider-bg]';

  const MASK_SELECTOR =
    '[data-layered-slider-mask]';

  const MASK_ITEM_SELECTOR =
    '[data-layered-slider-mask-item]';

  const FILL_SELECTOR =
    '[data-layered-slider-fill]';

  const CURRENT_SELECTOR =
    '[data-layered-slider-current]';

  const TOTAL_SELECTOR =
    '[data-layered-slider-total]';

  const PREV_SELECTOR =
    '[data-layered-slider-prev]';

  const NEXT_SELECTOR =
    '[data-layered-slider-next]';

  const EASE_NAME =
    'wambolt-layered-slider';

  const EASE_PATH =
    'M0,0 C0.625,0.05 0,1 1,1';

  const TRANSITION_DURATION = 1;
  const BACKGROUND_ZOOM = 0;
  const TITLE_GAP = 0.5;
  const TITLE_SPACING = 40;

  const DEPENDENCY_RETRY_LIMIT = 40;
  const DEPENDENCY_RETRY_DELAY = 100;

  const reducedMotionQuery =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  let dependenciesRegistered = false;
  let dependencyRetryTimer = null;
  let dependencyRetryAttempts = 0;


  /* =========================================
     DEPENDENCIES
     ========================================= */

  function registerDependencies() {
    if (
      !window.gsap ||
      !window.Observer ||
      !window.CustomEase
    ) {
      return false;
    }

    if (!dependenciesRegistered) {
      gsap.registerPlugin(
        Observer,
        CustomEase
      );

      CustomEase.create(
        EASE_NAME,
        EASE_PATH
      );

      dependenciesRegistered = true;
    }

    return true;
  }


  /* =========================================
     ROOT HELPERS
     ========================================= */

  function getRoots(
    scope = document
  ) {
    const roots = [];

    if (
      scope instanceof Element &&
      scope.matches(ROOT_SELECTOR)
    ) {
      roots.push(scope);
    }

    if (
      typeof scope.querySelectorAll ===
      'function'
    ) {
      roots.push(
        ...scope.querySelectorAll(
          ROOT_SELECTOR
        )
      );
    }

    return Array.from(
      new Set(roots)
    );
  }


  /* =========================================
     INSTANCE
     ========================================= */

  function createLayeredSlider(root) {
    if (!registerDependencies()) {
      return null;
    }

    if (
      root._layeredSlider &&
      typeof root._layeredSlider.destroy ===
        'function'
    ) {
      root._layeredSlider.destroy();
    }

    const titles =
      Array.from(
        root.querySelectorAll(
          TITLE_SELECTOR
        )
      );

    if (!titles.length) {
      return null;
    }

    const backgrounds =
      Array.from(
        root.querySelectorAll(
          BACKGROUND_SELECTOR
        )
      );

    const maskItems =
      Array.from(
        root.querySelectorAll(
          MASK_ITEM_SELECTOR
        )
      );

    const mediaVideos =
      Array.from(
        root.querySelectorAll(
          '.wambolt-layered-slider__background-video, .wambolt-layered-slider__mask-video'
        )
      );

    const maskFrame =
      root.querySelector(
        MASK_SELECTOR
      );

    const fill =
      root.querySelector(
        FILL_SELECTOR
      );

    const currentElement =
      root.querySelector(
        CURRENT_SELECTOR
      );

    const totalElement =
      root.querySelector(
        TOTAL_SELECTOR
      );

    const previousButton =
      root.querySelector(
        PREV_SELECTOR
      );

    const nextButton =
      root.querySelector(
        NEXT_SELECTOR
      );

    const count =
      titles.length;

    const controller =
      new AbortController();

    const { signal } =
      controller;

    const reducedMotion =
      reducedMotionQuery.matches;

    const clamp =
      gsap.utils.clamp;

    const sectionWrapper =
      root.closest(
        '.shopify-section'
      ) || root.parentElement;

    const originalWrapperPaddingBottom =
      sectionWrapper
        ? sectionWrapper.style.paddingBottom
        : '';

    const originalWrapperBackground =
      sectionWrapper
        ? {
            background:
              sectionWrapper.style.background,

            backgroundColor:
              sectionWrapper.style.backgroundColor,

            backgroundImage:
              sectionWrapper.style.backgroundImage,

            backgroundRepeat:
              sectionWrapper.style.backgroundRepeat,

            backgroundPosition:
              sectionWrapper.style.backgroundPosition,

            backgroundSize:
              sectionWrapper.style.backgroundSize,
          }
        : null;

    const controls =
      Array.from(
        new Set([
          ...titles,
          ...root.querySelectorAll(
            'a, button'
          ),
        ])
      );

    const autoplayAttribute =
      root.getAttribute(
        'data-layered-slider-autoplay'
      );

    const parsedAutoplay =
      autoplayAttribute !== null
        ? Number.parseFloat(
            autoplayAttribute
          )
        : 5;

    const autoplay =
      Number.isFinite(parsedAutoplay)
        ? Math.max(
            parsedAutoplay,
            0
          )
        : 5;

    const wrap =
      (distance) =>
        distance -
        count *
          Math.round(
            distance / count
          );

    let titleStep = 0;
    let maskStep = 0;
    let activeIndex = -1;
    let current = 0;
    let hovering = 0;
    let focusedWithin = false;
    let documentHidden =
      document.hidden;
    let isInView = true;
    let destroyed = false;

    let slideTween = null;
    let autoTween = null;
    let observer = null;
    let resizeObserver = null;
    let intersectionObserver = null;
    let layoutFrame = null;

    const state = {
      progress: 0,
    };


    /* =========================================
       COUNTER
       ========================================= */

    if (totalElement) {
      totalElement.textContent =
        String(count).padStart(
          2,
          '0'
        );
    }


    /* =========================================
       MEASUREMENT
       ========================================= */

    function measure() {
      const widestTitle =
        Math.max(
          ...titles.map(
            (title) =>
              title.offsetWidth
          )
        );

      titleStep =
        Math.max(
          root.clientWidth *
            TITLE_GAP,
          widestTitle +
            TITLE_SPACING
        );

      maskStep =
        maskFrame
          ? maskFrame.clientWidth
          : root.clientWidth;
    }


    /* =========================================
       SECTION BACKGROUND
       ========================================= */

    function syncWrapperBackground() {
      if (!sectionWrapper) {
        return;
      }

      const rootStyles =
        window.getComputedStyle(root);

      const schemeBackground =
        rootStyles
          .getPropertyValue(
            '--color-background'
          )
          .trim();

      const backgroundColor =
        schemeBackground ||
        rootStyles.backgroundColor;

      const backgroundImage =
        rootStyles.backgroundImage;

      const backgroundRepeat =
        rootStyles.backgroundRepeat;

      const backgroundPosition =
        rootStyles.backgroundPosition;

      const backgroundSize =
        rootStyles.backgroundSize;

      if (
        backgroundColor &&
        backgroundColor !==
          'rgba(0, 0, 0, 0)'
      ) {
        sectionWrapper.style.backgroundColor =
          backgroundColor;
      }

      sectionWrapper.style.backgroundImage =
        backgroundImage || 'none';

      sectionWrapper.style.backgroundRepeat =
        backgroundRepeat || 'repeat';

      sectionWrapper.style.backgroundPosition =
        backgroundPosition || '0% 0%';

      sectionWrapper.style.backgroundSize =
        backgroundSize || 'auto';
    }

    function restoreWrapperBackground() {
      if (
        !sectionWrapper ||
        !originalWrapperBackground
      ) {
        return;
      }

      sectionWrapper.style.background =
        originalWrapperBackground.background;

      sectionWrapper.style.backgroundColor =
        originalWrapperBackground.backgroundColor;

      sectionWrapper.style.backgroundImage =
        originalWrapperBackground.backgroundImage;

      sectionWrapper.style.backgroundRepeat =
        originalWrapperBackground.backgroundRepeat;

      sectionWrapper.style.backgroundPosition =
        originalWrapperBackground.backgroundPosition;

      sectionWrapper.style.backgroundSize =
        originalWrapperBackground.backgroundSize;
    }


    /* =========================================
       SECTION FLOW HEIGHT
       ========================================= */

    function restoreReservedSpace() {
      if (!sectionWrapper) {
        return;
      }

      sectionWrapper.style.paddingBottom =
        originalWrapperPaddingBottom;
    }

    function reserveForegroundOverflow() {
      if (
        !sectionWrapper ||
        !root.isConnected
      ) {
        return;
      }

      restoreReservedSpace();

      const wrapperRect =
        sectionWrapper
          .getBoundingClientRect();

      const overflowCandidates =
        [
          maskFrame,
          ...maskItems,
        ].filter(
          (element) =>
            element instanceof Element &&
            element.isConnected
        );

      if (!overflowCandidates.length) {
        return;
      }

      const lowestEdge =
        Math.max(
          ...overflowCandidates.map(
            (element) =>
              element
                .getBoundingClientRect()
                .bottom
          )
        );

      const overflow =
        Math.max(
          0,
          Math.ceil(
            lowestEdge -
            wrapperRect.bottom
          )
        );

      if (overflow <= 0) {
        return;
      }

      const basePadding =
        Number.parseFloat(
          window
            .getComputedStyle(
              sectionWrapper
            )
            .paddingBottom
        ) || 0;

      sectionWrapper.style.paddingBottom =
        `${basePadding + overflow}px`;
    }

    function scheduleLayoutSync() {
      if (
        destroyed ||
        layoutFrame !== null
      ) {
        return;
      }

      layoutFrame =
        window.requestAnimationFrame(
          () => {
            layoutFrame = null;

            if (
              destroyed ||
              !root.isConnected
            ) {
              return;
            }

            measure();

            render(
              state.progress
            );

            syncWrapperBackground();
            reserveForegroundOverflow();
          }
        );
    }


    /* =========================================
       ACTIVE STATE
       ========================================= */

    function setActive(
      previousIndex,
      index
    ) {
      [
        backgrounds,
        titles,
        maskItems,
      ].forEach((list) => {
        const previousElement =
          previousIndex >= 0
            ? list[
                previousIndex
              ]
            : null;

        const activeElement =
          list[index];

        if (previousElement) {
          previousElement
            .removeAttribute(
              'data-active'
            );

          if (
            list === titles
          ) {
            previousElement
              .removeAttribute(
                'aria-current'
              );
          }
        }

        if (activeElement) {
          activeElement
            .setAttribute(
              'data-active',
              ''
            );

          if (
            list === titles
          ) {
            activeElement
              .setAttribute(
                'aria-current',
                'true'
              );
          }
        }
      });

      syncMediaPlayback();
    }


    /* =========================================
       OPTIONAL VIDEO PLAYBACK
       ========================================= */

    function canPlayMediaVideo(video) {
      const slide =
        video.closest(
          '[data-layered-slider-bg], [data-layered-slider-mask-item]'
        );

      return (
        !destroyed &&
        !reducedMotion &&
        !documentHidden &&
        isInView &&
        root.isConnected &&
        slide?.hasAttribute('data-active')
      );
    }

    function syncMediaPlayback() {
      mediaVideos.forEach((video) => {
        if (!canPlayMediaVideo(video)) {
          if (!video.paused) {
            video.pause();
          }
          return;
        }

        if (video.paused) {
          try {
            const playPromise = video.play();

            if (playPromise?.catch) {
              playPromise.catch(() => {
                if (video.paused) {
                  video.classList.remove('is-playing');
                }
              });
            }
          } catch (error) {
            video.classList.remove('is-playing');
          }
        }
      });
    }

    mediaVideos.forEach((video) => {
      video.addEventListener(
        'playing',
        () => {
          if (canPlayMediaVideo(video)) {
            video.classList.add('is-playing');
          } else {
            video.pause();
          }
        },
        { signal }
      );

      video.addEventListener(
        'error',
        () => video.classList.remove('is-playing'),
        { signal }
      );
    });


    /* =========================================
       RENDER
       ========================================= */

    function render(progress) {
      const centeredIndex =
        (
          (
            Math.round(
              progress
            ) % count
          ) + count
        ) % count;

      for (
        let index = 0;
        index < count;
        index += 1
      ) {
        const offset =
          wrap(
            index - progress
          );

        const distance =
          Math.abs(offset);

        const background =
          backgrounds[index];

        if (background) {
          const backgroundOpacity =
            clamp(
              0,
              1,
              1 - distance
            );

          gsap.set(
            background,
            {
              opacity:
                backgroundOpacity,

              scale:
                1 +
                BACKGROUND_ZOOM -
                BACKGROUND_ZOOM *
                  backgroundOpacity,

              zIndex:
                Math.round(
                  backgroundOpacity *
                    100
                ),
            }
          );
        }

        gsap.set(
          titles[index],
          {
            x:
              offset *
              titleStep,

            opacity:
              index ===
              centeredIndex
                ? 1
                : 0.4,

            pointerEvents:
              'auto',
          }
        );

        const maskItem =
          maskItems[index];

        if (maskItem) {
          gsap.set(
            maskItem,
            {
              x:
                offset *
                maskStep,
            }
          );
        }
      }

      if (
        centeredIndex ===
        activeIndex
      ) {
        return;
      }

      const previousIndex =
        activeIndex;

      activeIndex =
        centeredIndex;

      setActive(
        previousIndex,
        centeredIndex
      );

      if (currentElement) {
        currentElement.textContent =
          String(
            centeredIndex + 1
          ).padStart(
            2,
            '0'
          );
      }
    }


    /* =========================================
       AUTOPLAY
       ========================================= */

    function shouldPauseAutoplay() {
      return (
        hovering > 0 ||
        focusedWithin ||
        documentHidden ||
        !isInView
      );
    }

    function syncAutoplay() {
      if (!autoTween) {
        return;
      }

      if (
        shouldPauseAutoplay()
      ) {
        autoTween.pause();
      } else {
        autoTween.resume();
      }
    }

    function restartAutoplay() {
      if (!autoTween) {
        return;
      }

      autoTween.restart();
      syncAutoplay();
    }


    /* =========================================
       NAVIGATION
       ========================================= */

    function goTo(delta) {
      current += delta;

      if (slideTween) {
        slideTween.kill();
      }

      slideTween =
        gsap.to(
          state,
          {
            progress:
              current,

            duration:
              reducedMotion
                ? 0
                : TRANSITION_DURATION,

            ease:
              EASE_NAME,

            overwrite:
              true,

            onUpdate() {
              render(
                state.progress
              );
            },

            onComplete() {
              slideTween = null;
              scheduleLayoutSync();
            },
          }
        );

      restartAutoplay();
    }

    function goToIndex(index) {
      const delta =
        wrap(
          index - current
        );

      if (delta !== 0) {
        goTo(delta);
      }
    }


    /* =========================================
       AUTOPLAY TIMELINE
       ========================================= */

    if (
      autoplay > 0 &&
      !reducedMotion &&
      fill &&
      count > 1
    ) {
      gsap.set(
        fill,
        {
          scaleX: 0,
          transformOrigin:
            'left center',
        }
      );

      autoTween =
        gsap.to(
          fill,
          {
            scaleX: 1,
            duration:
              autoplay,
            ease: 'none',
            paused: true,

            onComplete() {
              goTo(1);
            },
          }
        );
    }


    /* =========================================
       POINTER GESTURE
       ========================================= */

    if (count > 1) {
      let gestureUsed =
        false;

      observer =
        Observer.create({
          target: root,

          type:
            'touch,pointer',

          dragMinimum: 10,
          tolerance: 25,
          lockAxis: true,

          onDragStart() {
            gestureUsed =
              false;
          },

          onLeft() {
            if (gestureUsed) {
              return;
            }

            gestureUsed =
              true;

            goTo(1);
          },

          onRight() {
            if (gestureUsed) {
              return;
            }

            gestureUsed =
              true;

            goTo(-1);
          },
        });
    }


    /* =========================================
       BUTTONS
       ========================================= */

    previousButton?.addEventListener(
      'click',
      () => {
        goTo(-1);
      },
      {
        signal,
      }
    );

    nextButton?.addEventListener(
      'click',
      () => {
        goTo(1);
      },
      {
        signal,
      }
    );


    /* =========================================
       TITLES
       ========================================= */

    titles.forEach(
      (title, index) => {
        title.addEventListener(
          'click',
          (event) => {
            if (
              index ===
              activeIndex
            ) {
              return;
            }

            event.preventDefault();
            goToIndex(index);
          },
          {
            signal,
          }
        );
      }
    );


    /* =========================================
       KEYBOARD
       ========================================= */

    root.addEventListener(
      'keydown',
      (event) => {
        if (count < 2) {
          return;
        }

        const target =
          event.target;

        if (
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement ||
          target?.isContentEditable
        ) {
          return;
        }

        if (
          event.key ===
          'ArrowLeft'
        ) {
          event.preventDefault();
          goTo(-1);
          return;
        }

        if (
          event.key ===
          'ArrowRight'
        ) {
          event.preventDefault();
          goTo(1);
          return;
        }

        if (
          event.key ===
          'Home'
        ) {
          event.preventDefault();
          goToIndex(0);
          return;
        }

        if (
          event.key ===
          'End'
        ) {
          event.preventDefault();
          goToIndex(
            count - 1
          );
        }
      },
      {
        signal,
      }
    );


    /* =========================================
       AUTOPLAY PAUSE STATES
       ========================================= */

    controls.forEach(
      (element) => {
        element.addEventListener(
          'pointerenter',
          () => {
            hovering += 1;
            syncAutoplay();
          },
          {
            signal,
          }
        );

        element.addEventListener(
          'pointerleave',
          () => {
            hovering =
              Math.max(
                0,
                hovering - 1
              );

            syncAutoplay();
          },
          {
            signal,
          }
        );
      }
    );

    root.addEventListener(
      'focusin',
      () => {
        focusedWithin = true;
        syncAutoplay();
      },
      {
        signal,
      }
    );

    root.addEventListener(
      'focusout',
      (event) => {
        const nextFocus =
          event.relatedTarget;

        focusedWithin =
          nextFocus instanceof Node &&
          root.contains(
            nextFocus
          );

        syncAutoplay();
      },
      {
        signal,
      }
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        documentHidden =
          document.hidden;

        syncAutoplay();
        syncMediaPlayback();
      },
      {
        signal,
      }
    );


    /* =========================================
       VIEWPORT VISIBILITY
       ========================================= */

    if (
      'IntersectionObserver' in
      window
    ) {
      intersectionObserver =
        new IntersectionObserver(
          (entries) => {
            const entry =
              entries[0];

            if (!entry) {
              return;
            }

            isInView =
              entry.isIntersecting;

            syncAutoplay();
            syncMediaPlayback();
          },
          {
            threshold: 0.01,
          }
        );

      intersectionObserver.observe(
        root
      );
    }


    /* =========================================
       RESPONSIVE LAYOUT SYNC
       ========================================= */

    window.addEventListener(
      'resize',
      scheduleLayoutSync,
      {
        passive: true,
        signal,
      }
    );

    if (
      'ResizeObserver' in window
    ) {
      resizeObserver =
        new ResizeObserver(
          scheduleLayoutSync
        );

      resizeObserver.observe(root);

      if (maskFrame) {
        resizeObserver.observe(
          maskFrame
        );
      }
    }

    root
      .querySelectorAll('img')
      .forEach((image) => {
        if (!image.complete) {
          image.addEventListener(
            'load',
            scheduleLayoutSync,
            {
              once: true,
              signal,
            }
          );
        }
      });

    if (
      document.fonts &&
      document.fonts.ready
    ) {
      document.fonts.ready.then(
        () => {
          if (
            !destroyed &&
            root.isConnected
          ) {
            scheduleLayoutSync();
          }
        }
      );
    }


    /* =========================================
       INITIAL STATE
       ========================================= */

    syncWrapperBackground();
    measure();
    render(0);
    restartAutoplay();
    scheduleLayoutSync();


    /* =========================================
       DESTROY
       ========================================= */

    const instance = {
      goTo,
      goToIndex,

      destroy() {
        if (destroyed) {
          return;
        }

        destroyed = true;

        mediaVideos.forEach((video) => {
          video.pause();
          video.classList.remove('is-playing');
        });

        controller.abort();

        observer?.kill();
        observer = null;

        slideTween?.kill();
        slideTween = null;

        autoTween?.kill();
        autoTween = null;

        resizeObserver?.disconnect();
        resizeObserver = null;

        intersectionObserver?.disconnect();
        intersectionObserver = null;

        if (layoutFrame !== null) {
          window.cancelAnimationFrame(
            layoutFrame
          );

          layoutFrame = null;
        }

        restoreReservedSpace();
        restoreWrapperBackground();

        gsap.killTweensOf(state);
        gsap.killTweensOf(backgrounds);
        gsap.killTweensOf(titles);
        gsap.killTweensOf(maskItems);

        root._layeredSlider =
          null;
      },
    };

    root._layeredSlider =
      instance;

    return instance;
  }


  /* =========================================
     COMPONENT LIFECYCLE
     ========================================= */

  function initLayeredImageSlider(
    scope = document
  ) {
    const roots =
      getRoots(scope);

    if (!roots.length) {
      return true;
    }

    if (!registerDependencies()) {
      return false;
    }

    roots.forEach(
      createLayeredSlider
    );

    return true;
  }

  function destroyLayeredImageSlider(
    scope = document
  ) {
    getRoots(scope).forEach(
      (root) => {
        if (
          root._layeredSlider &&
          typeof root._layeredSlider.destroy ===
            'function'
        ) {
          root._layeredSlider.destroy();
        }
      }
    );
  }

  function rebuildLayeredImageSliders() {
    destroyLayeredImageSlider(
      document
    );

    if (
      !initLayeredImageSlider(
        document
      )
    ) {
      scheduleDependencyRetry();
    }
  }


  /* =========================================
     DEPENDENCY-SAFE START
     ========================================= */

  function clearDependencyRetry() {
    if (
      dependencyRetryTimer === null
    ) {
      return;
    }

    window.clearTimeout(
      dependencyRetryTimer
    );

    dependencyRetryTimer = null;
  }

  function scheduleDependencyRetry() {
    if (
      dependencyRetryTimer !== null
    ) {
      return;
    }

    if (
      dependencyRetryAttempts >=
      DEPENDENCY_RETRY_LIMIT
    ) {
      console.warn(
        'Wambolt Layered Slider could not initialize because GSAP, Observer, or CustomEase is unavailable.'
      );

      return;
    }

    dependencyRetryAttempts += 1;

    dependencyRetryTimer =
      window.setTimeout(
        () => {
          dependencyRetryTimer = null;

          if (
            initLayeredImageSlider(
              document
            )
          ) {
            dependencyRetryAttempts = 0;
            clearDependencyRetry();
            return;
          }

          scheduleDependencyRetry();
        },
        DEPENDENCY_RETRY_DELAY
      );
  }

  function start() {
    if (
      initLayeredImageSlider(
        document
      )
    ) {
      dependencyRetryAttempts = 0;
      clearDependencyRetry();
      return;
    }

    scheduleDependencyRetry();
  }


  /* =========================================
     INITIAL LOAD
     ========================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true,
      }
    );
  } else {
    start();
  }


  /* =========================================
     REDUCED MOTION CHANGES
     ========================================= */

  if (
    typeof reducedMotionQuery.addEventListener ===
    'function'
  ) {
    reducedMotionQuery.addEventListener(
      'change',
      rebuildLayeredImageSliders
    );
  } else if (
    typeof reducedMotionQuery.addListener ===
    'function'
  ) {
    reducedMotionQuery.addListener(
      rebuildLayeredImageSliders
    );
  }


  /* =========================================
     SHOPIFY / BARBA LIFECYCLE
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (
        !initLayeredImageSlider(
          event.target
        )
      ) {
        scheduleDependencyRetry();
      }
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyLayeredImageSlider(
        event.target
      );
    }
  );

  document.addEventListener(
    'shopify:block:select',
    (event) => {
      const root =
        event.target.closest?.(
          ROOT_SELECTOR
        );

      if (!root) {
        return;
      }

      const title =
        event.target.matches?.(
          TITLE_SELECTOR
        )
          ? event.target
          : event.target.querySelector?.(
              TITLE_SELECTOR
            );

      if (!title) {
        return;
      }

      const titles =
        Array.from(
          root.querySelectorAll(
            TITLE_SELECTOR
          )
        );

      const index =
        titles.indexOf(title);

      if (
        index >= 0 &&
        root._layeredSlider
      ) {
        root._layeredSlider
          .goToIndex(index);
      }
    }
  );
})();
